<script setup lang="ts">
import {
  iKeyIcon,
  iKeyIconSize,
  iKeyIconShape,
  iKeyIconStyle,
  iKeyIconType,
} from '@inscribe/signal';
import { CustomerInsightDailyBalance } from '@/utils/customerInsights';
import ExpandableContainer from '@/V2/components/expandable-container/ExpandableContainer.vue';
import ExpandableContainerItem from '@/V2/components/expandable-container/ExpandableContainerItem.vue';
import { toCurrency } from '@/utils/toCurrency';
import { useRouter } from 'vue-router';

/** Refs */
const router = useRouter();

/** Props */
const props = defineProps({
  dailyBalances: {
    type: Array<CustomerInsightDailyBalance>,
    required: true,
  },
});

/** Helpers */
const getDailyBalanceTitle = (dailyBalance: CustomerInsightDailyBalance) => {
  return `${toCurrency(dailyBalance.balance, 'USD')}`;
};

const getDailyBalanceSubtitle = (dailyBalance: CustomerInsightDailyBalance) => {
  return `${dailyBalance.period_start} - ${dailyBalance.period_end}`;
};

/** Handlers */
const onDailyBalanceClick = () => {
  router.push({
    name: 'customer-credit-transactions',
    params: { customer_id: router?.currentRoute?.value?.params?.customer_id },
  });
};

/** Expose */
defineExpose({
  onDailyBalanceClick,
  iKeyIconSize,
  iKeyIconShape,
  iKeyIconStyle,
  iKeyIconType,
});
</script>

<template>
  <expandable-container
    title="Matching Accounts"
    :expandable="false"
    class="signal-daily-balances-list"
  >
    <!-- chart -->
    <template #items>
      <div class="daily-balances-list d-flex flex-column">
        <expandable-container-item
          v-for="dailyBalance in dailyBalances"
          :key="dailyBalance.bank_account_uuid"
          :title="getDailyBalanceTitle(dailyBalance)"
          :subtitle="getDailyBalanceSubtitle(dailyBalance)"
          aria-label="Expandable Container Item: Daily Balance Account"
          data-dd-action-name="expandable-item-daily-balance"
          @on-item-click="onDailyBalanceClick"
          @on-right-icon-click="onDailyBalanceClick"
        >
          <template #left-container>
            <i-key-icon
              icon="medium_money_atm_card_1"
              :size="iKeyIconSize.MEDIUM"
              :shape="iKeyIconShape.SQUARE"
              :icon-style="iKeyIconStyle.OUTLINE"
              :type="iKeyIconType.NEUTRAL"
            />
          </template>

          <template #right-container>
            <div
              class="account-badge d-flex align-items-center i-gap-4 i-px-3 i-py-2 i-radius-sm"
            >
              <div class="i-text-body-3 label">Account</div>
              <div class="i-text-button-3 value">
                ****{{ dailyBalance.bank_account_last_four }}
              </div>
            </div>
          </template>
        </expandable-container-item>
      </div>
    </template>
  </expandable-container>
</template>

<style lang="scss" scoped>
.signal-daily-balances-list {
  .daily-balances-list {
    gap: 1px;
    background: var(--surface-neutral);

    .expandable-item {
      width: 100%;
    }
  }

  .account-badge {
    background-color: var(--fill-neutral-subtle);
    .label {
      color: var(--text-neutral-bold);
    }
    .value {
      color: var(--text-neutral-bold);
    }
  }
}
</style>
